'use client';

import { useId } from '@react-aria/utils';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import type { CommonProps } from '../types.js';
import { TableRowStateProvider } from './contexts/row-state.js';

export interface TableHeaderRowProps extends CommonProps {
  /** The header cells that belong to this header row. */
  children: ReactNode;
  /**
   * The row's position within the full dataset, including headers.
   *
   * Provide this value if your table presents a subset of rows, such as when using pagination.
   *
   * This number must be:
   * - greater than or equal to `1`
   * - greater than the `rowNumber` of any previous rows
   * - less than or equal to the `totalRowCount` of the parent `Table`
   *
   * Use together with `totalRowCount` on the `Table` component.
   */
  rowNumber?: number;
}

function TableHeaderRow(
  { children, UNSAFE_className, UNSAFE_style, rowNumber, ...props }: TableHeaderRowProps,
  ref: ForwardedRef<HTMLTableRowElement>,
) {
  const rowKey = useId();

  return (
    <tr {...props} ref={ref} className={UNSAFE_className} style={UNSAFE_style} aria-rowindex={rowNumber}>
      <TableRowStateProvider rowKey={rowKey}>{children}</TableRowStateProvider>
    </tr>
  );
}

/**
 * Renders a row within a table header.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableHeaderRow = forwardRef(TableHeaderRow, 'TableHeaderRow');

export { _TableHeaderRow as TableHeaderRow };
